import React, {useState} from 'react';
import {useRsvp} from '../context/RsvpContext';
import RSVPApiService from '../services/rsvpApiService';
import {Link, Navigate, useNavigate} from "react-router-dom";
import CustomButton from "../components/CustomButton";
import RSVPCard from "../components/RSVPCard";
import {cardData} from "../components/CardsDisplay";
import {Guest} from "../../rsvp-backend/src/types/interfaces";
import Alert from "../components/Alert";

const RSVPPage: React.FC = () => {
    const {rsvp, setRsvp} = useRsvp();
    const [song, setSong] = useState<string>(rsvp?.songRequest || '');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [messageType, setMessageType] = useState<'success' | 'error' | 'info' | 'warning'>('info');
    const [guests, setGuests] = useState<Guest[]>(rsvp?.guests || []);
    const navigate = useNavigate();

    if (!rsvp) {
        // If someone lands here without session, send back to gate
        return <Navigate to="/" replace />;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            const updated = {
                ...rsvp,
                songRequest: song, 
                RSVPStatus: 'Answered',
                rsvpPrimerMes: guests?.find(guest => guest.considered)?.considered ? 'Confirmed' : 'Declined',
                guests: guests
            };
            // Update via API service
            const rsvpApiService = RSVPApiService;
            const updatedRsvp = await rsvpApiService.updateRSVP(updated);
            // Update with the response from API
            setRsvp(updated);
            setGuests(updated.guests);
            sessionStorage.setItem('rsvp', JSON.stringify(updatedRsvp));
            setMessage('¡Gracias! Tu confirmación fue guardada.');
            setMessageType('success');
        } catch (e: any) {
            setMessage('Ocurrió un error al guardar. Intenta más tarde.');
            setMessageType('error');
        } finally {
            setSaving(false);
        }
    };

    const handleNavigate = () => {
        navigate("/info", { replace: true, state: { transport: true } });
    };

    const card = cardData[1];

    return (
        <section className="min-h-screen py-16 bg-[#8a2c47]">
            <video
                className="inset-0 w-lvw h-lvh object-cover fixed"
                style={{mixBlendMode: 'multiply'}}
                autoPlay loop muted playsInline>
                <source src="/Leaves.mp4" type="video/mp4"/>
                Your browser does not support the video tag.
            </video>
            <div className="container mx-auto px-4 max-w-2xl relative z-10">
                <h2 className="text-[30pt] md:text-[45pt] text-white text-center font-bold -mb-2 md:-mb-6">RSVP</h2>
                <p className="text-white text-center mb-6 font-extralight text-[18pt] md:text-[22pt]">Primera Fase</p>
                <p className="px-10 mb-4 text-white font-light">
                    Por favor confirma tu asistencia.<br/>
                    Revisa si los datos a continuación son correctos y llena el formulario.
                </p>

                {guests.map((invitado, index) => (
                    (invitado.editable || invitado.considered || invitado.name !== '') && (
                        <RSVPCard
                            index={index}
                            key={index.toString()}
                            guestName={invitado.name}
                            isNameEditable={invitado.editable}
                            onNameChange={(name: string) => {
                                const newGuests = [...guests];
                                newGuests[index].name = name;
                                setGuests(newGuests);
                            }}
                            onAttendanceChange={(attendance: string) => {
                                const newGuests = [...guests];
                                newGuests[index].considered = attendance === 'Asistirá';
                                setGuests(newGuests);
                            }}
                            onDietaryRestrictionChange={(restriction: string) => {
                                const newGuests = [...guests];
                                newGuests[index].dietaryRestrictions = restriction;
                                setGuests(newGuests);
                            }}
                            onDietaryDetailsChange={(details: string) => {
                                const newGuests = [...guests];
                                newGuests[index].specificDiet = details;
                                setGuests(newGuests);
                            }}
                            onAllergiesChange={(hasAllergies: boolean) => {
                                const newGuests = [...guests];
                                newGuests[index].hasAllergy = hasAllergies;
                                setGuests(newGuests);
                            }}
                            onAllergyDetailsChange={(allergyDetails: string) => {
                                const newGuests = [...guests];
                                newGuests[index].allergyDescription = allergyDetails;
                                setGuests(newGuests);
                            }}
                            initialAttendance={invitado.considered ? 'Asistirá' : 'No asistirá'}
                            initialDietaryRestriction={invitado.dietaryRestrictions !== '' ? invitado.dietaryRestrictions : 'No Aplica'}
                            initialDietaryDetails={invitado.specificDiet}
                            initialHasAllergies={invitado.hasAllergy}
                            initialAllergyDetails={invitado.allergyDescription}
                        />
                    )
                ))}

                <div className="p-10 mt-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block mb-2 font-light text-white">Con esta canción salgo a bailar a la pista</label>
                            <input
                                className="w-full bg-white rounded-md px-4 py-2"
                                placeholder="Nombre de la(s) canción(es)"
                                value={song}
                                onChange={(e) => setSong(e.target.value)}
                            />
                        </div>

                        {message && <Alert
                            severity={messageType}
                            onClose={() => setMessage(null)}
                            className="shadow-sm"
                        >
                            {message}
                        </Alert>}
                        <div className="flex justify-center">
                            <CustomButton
                                type="submit"
                                text={saving ? 'GUARDANDO...' : 'ENVIAR \u00A0\u00A0 RSVP'}
                                disabled={saving}
                                rounded={'rounded-full'}
                                bgColor={'bg-(--hover-color)'}
                                hoverColor={'hover:bg-(--primary-color)'}
                                className="col-start-2 font-bold"
                            />
                        </div>
                    </form>
                </div>
                <div className="grid grid-rows-2 grid-cols-2 gap-4">
                    {!card.enabled ? (
                        <Link to="" key={card.path} className="row-span-2 md:mt-0 mt-8">
                            <div className="relative">
                                <img
                                    src="/images/Grid-Transporte%20-%20Copy.png"
                                    alt={card.title}
                                    className="transition-transform duration-500 ease-in-out rounded-2xl filter grayscale"
                                />
                                <img
                                    src={card.prox}
                                    alt={card.title + " prox"}
                                    className="absolute inset-0 pointer-events-none rounded-2xl object-cover"
                                />
                            </div>
                        </Link>
                    ) : (
                        <Link to={card.path} key={card.path}
                              className="row-span-2">
                            <div className="">
                                <img
                                    src="/images/Grid-Transporte%20-%20Copy.png"
                                    alt={card.title}
                                    className="w-full transition-transform duration-500 ease-in-out group-hover:scale-105 rounded-2xl"
                                />
                            </div>
                        </Link>
                    )}
                    <label className="font-light text-white md:mt-8">Si aún no estás segur@ de tu forma
                        de transporte y estarías interesad@ en contratar transporte privado, ve a la sección de
                        transporte.</label>
                    <div className="flex justify-center items-center md:-mt-16 -mt-32">
                        <CustomButton
                            text={'Ver transporte'}
                            onClick={handleNavigate}
                            disabled={!card.enabled}
                            rounded={'rounded-full'}
                            bgColor={'bg-[#5c1a27]'}
                            hoverColor={'hover:bg-(--hover-color)'}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default RSVPPage;
